import { arrayOf, bool, func, string } from 'prop-types';
import React from 'react';
import {
  Button,
  Collapse,
  Input,
  ListGroup,
  ListGroupItem,
  ModalBody,
  ModalHeader,
} from 'reactstrap';

import createModalToggle from '../../../generic/create-modal-toggle';
import { contactListShape } from '../../../prop-types';
import add from '../../../REST/add';
import api from '../../../REST/api';
import withApiRequest from '../../../REST/with-api-request';
import withResolved from '../../../REST/with-resolved';
import { COLORS } from '../../../utils/constants';
import { tapFlashSuccess } from '../../../utils/flash';
import getEventTargetValue from '../../../utils/get-event-target-value';
import replaceById from '../../../utils/replace-by-id';
import ContactListForm from '../ContactListForm';
import { ContactListLink } from '../Link';
import AddToContactListButton from './AddToContactListButton';

const AddContactList = add({
  update: (props, model) =>
    api.addContactList({
      data: model,
    }),
  form: ContactListForm,
});

function getNewContactList(selectedContactIds) {
  return { name: '', description: '', contactIds: selectedContactIds };
}

class AddToContactListModalContent extends React.Component {
  state = {
    lists: this.props.lists,
    isNewFormOpen: false,
    searchText: '',
  };

  handleNewList = list => {
    this.setState(state => ({
      lists: [list].concat(state.lists),
      isNewFormOpen: false,
      searchText: '',
    }));
    tapFlashSuccess('Created a new list with selected contacts.')();
  };

  handleNewFormToggle = () => {
    this.setState(state => ({
      isNewFormOpen: !state.isNewFormOpen,
    }));
  };

  handleSearchTextChange = e => {
    this.setState({
      searchText: getEventTargetValue(e.target),
    });
  };

  handleAddToList(list) {
    const { selectedContactIds, onRequest } = this.props;
    const currentContactIds = list.contactIds || [];
    const newContactIds = selectedContactIds.map(id => Number(id));
    onRequest({
      data: {
        contactIds: currentContactIds.concat(newContactIds),
      },
      id: list.id,
    }).then(response => {
      this.setState(state => ({ lists: replaceById(state.lists, response) }));
    });
  }

  render() {
    const { toggle, selectedContactIds, loading } = this.props;
    const { lists, isNewFormOpen, searchText } = this.state;
    const listsToDisplay = lists.filter(({ name, description }) => {
      const query = searchText.toLowerCase();
      return (
        name.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query)
      );
    });
    return (
      <>
        <ModalHeader toggle={toggle} className="w-100">
          Add {selectedContactIds.length} contact(s) to a list
        </ModalHeader>
        <ModalBody>
          <Input
            name="searchText"
            value={searchText}
            onChange={this.handleSearchTextChange}
            placeholder="Type to filter…"
            className="d-inline-block"
          />
          <div className="mb-3" />
          <ListGroup>
            <ListGroupItem>
              <Button
                color={COLORS.LINK}
                className="p-0"
                onClick={this.handleNewFormToggle}
              >
                Create a new list
              </Button>
              <Collapse isOpen={isNewFormOpen}>
                <div className="mb-3" />
                <AddContactList
                  key={lists.length}
                  default={getNewContactList(selectedContactIds)}
                  onSuccess={this.handleNewList}
                />
              </Collapse>
            </ListGroupItem>
            {listsToDisplay.map(list => {
              const { id, description } = list;
              return (
                <ListGroupItem key={id}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h3 className="h6">
                        <ContactListLink model={list} />
                      </h3>
                      <p className="small text-muted m-0">{description}</p>
                    </div>
                    <AddToContactListButton
                      list={list}
                      disabled={loading}
                      selectedContactIds={selectedContactIds}
                      className="ml-5 text-nowrap"
                      onClick={() => this.handleAddToList(list)}
                    />
                  </div>
                </ListGroupItem>
              );
            })}
          </ListGroup>
        </ModalBody>
      </>
    );
  }
}

AddToContactListModalContent.propTypes = {
  lists: arrayOf(contactListShape.isRequired).isRequired,
  toggle: func.isRequired,
  onRequest: func.isRequired,
  loading: bool.isRequired,
  selectedContactIds: arrayOf(string.isRequired).isRequired,
};

const withPatchRequest = withApiRequest({
  query: api.patchContactList,
  error: 'flash',
  successMessage: 'Selected Contact(s) added to the list',
})(AddToContactListModalContent);

const modalContentWithContactLists = withResolved({
  query: api.getContactLists,
  as: 'lists',
})(withPatchRequest);

export default createModalToggle(
  'Add selected contacts to a list',
  modalContentWithContactLists,
  { className: 'text-nowrap' },
  { size: 'lg' },
);
